// Portado del prototipo del Brain (`dont-merge-front-vision`), con su nombre y
// su codigo.
//
// **El clip y el microfono se pintan solo si les pasas su manejador**, que es
// como lo escribio el prototipo. Hoy la pagina no se los pasa: el Brain no
// acepta adjuntos, y el microfono estuvo en esta pantalla hasta el 2026-08-25
// grabando de verdad para escribir «Transcripcion de audio (demo)» en la caja.
// Cuando exista el backend, basta pasar el manejador y vuelven a aparecer.

import React, { useEffect, useRef } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { DictationWave } from '@/components/brain/DictationWave'
import { useTranslation } from 'react-i18next'
import { ModelSelector } from './ModelSelector'
import type { ChosenModel } from '@/services/brainApi'
import type { AgentAvailableModel } from '@/types/agent'
import { Loader2, Mic, MicOff, Paperclip, Send, Square, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AgentSelector } from './AgentSelector'
import { agentColor } from './helpers'
import { AttachmentCard } from './AttachmentCard'
import type { Agent, FileInfo } from './types'

export function ChatInput({ agents, inputRef: refDeFuera, inputValue, setInputValue, isLoading, onSend, onStop, onToggleRecording, isRecording, isTranscribing, recordingStream, placeholder, addMessagePlaceholder, large, pendingFiles, onRemoveFile, onAttach, activeAgent, onAgentSelect, models, chosenModel, onModelSelect }: {
  /** Los agentes a los que se puede hablar. En el prototipo era una constante.
   *  **Opcional desde 2.7**: el selector se pinta solo si le pasas a quien
   *  avisar, igual que el clip y el microfono. En el panel no se elige agente
   *  -se habla con el Brain-, y esa decision es de la pantalla completa. */
  agents?: Agent[]
  inputRef?: React.RefObject<HTMLTextAreaElement | null>
  inputValue: string
  setInputValue: (v: string) => void
  isLoading: boolean
  // Se llama **sin argumentos**. Tipado `() => void`, un `onClick={onSend}`
  // compila -una funcion sin parametros vale donde se espera una que acepta
  // uno- y el manejador recibia el SyntheticEvent como `message`: se pintaba
  // el evento (React #31, pantalla en negro) y se intentaba serializar
  // (`circular structure ... Window`). El tipo dice ahora lo que la pagina
  // recibe, para que el compilador lo vea.
  onSend: (message?: string) => void
  /** Detener lo que el Brain esta haciendo. Con el, mientras piensa, el boton de
   *  enviar pasa a ser el de detener: sin esto, un mensaje en marcha no se
   *  podia parar desde que todo mensaje se trabaja en segundo plano. */
  onStop?: () => void
  onToggleRecording?: () => void
  isRecording?: boolean
  /** The dictation is being turned into text (brain-multimodal, D7). */
  isTranscribing?: boolean
  /** The live microphone while dictating: the box shows its wave instead. */
  recordingStream?: MediaStream | null
  placeholder: string
  addMessagePlaceholder: string
  large?: boolean
  pendingFiles?: FileInfo[]
  onRemoveFile?: (idx: number) => void
  onAttach?: () => void
  activeAgent?: Agent | null
  onAgentSelect?: (agent: Agent | null) => void
  /** Los modelos que se pueden elegir para esta conversación (7.2). Como el
   *  selector de agente, se pinta solo si le pasas a quién avisar. */
  models?: AgentAvailableModel[]
  chosenModel?: ChosenModel | null
  onModelSelect?: (model: ChosenModel | null) => void
}) {
  // La caja crece sola, y para eso necesita un ref. Si quien la usa no
  // quiere el suyo -el panel no lo necesita-, se pone uno aqui.
  const refPropio = useRef<HTMLTextAreaElement>(null)
  const inputRef = refDeFuera ?? refPropio

  const { t } = useTranslation()
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 160)}px`
    }
  }, [inputValue, inputRef])

  const hasPending = pendingFiles && pendingFiles.length > 0
  const canSend = inputValue.trim() || hasPending

  return (
    <div className={cn(
      "w-full rounded-lg border bg-muted/30 shadow-sm backdrop-blur-xl transition-all",
      "focus-within:border-primary/40 focus-within:bg-background/80 focus-within:shadow-md focus-within:",
      activeAgent && "border-border",
      large && "max-w-2xl"
    )}>
      {/* Active agent strip — thin colored bar at top when an agent is selected */}
      {activeAgent && (
        <div className={cn(
          "flex items-center gap-2 rounded-t-lg border-b border-border px-3.5 py-1.5",
          "bg-gradient-to-r from-muted/60 to-transparent"
        )}>
          <span className={cn(
            "flex h-4 w-4 shrink-0 items-center justify-center rounded-md text-xs font-bold leading-none",
            agentColor(activeAgent.name),
          )}>
            {(activeAgent.display_name || activeAgent.name).slice(0, 1).toUpperCase()}
          </span>
          <span className="text-xs font-medium text-foreground/60">
            {t('brain.hablando_con')}{' '}
            <span className="font-semibold text-foreground/80">
              {activeAgent.display_name || activeAgent.name}
            </span>
          </span>
          <Button
            variant="link"
            onClick={() => onAgentSelect?.(null)}
            className="ml-auto text-muted-foreground hover:text-foreground"
            title="Volver a Brain"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Pending file chips */}
      {hasPending && (
        <div className="flex flex-wrap gap-2 px-3.5 pt-3.5">
          {pendingFiles!.map((f, i) => (
            <AttachmentCard key={i} file={f} removable onRemove={() => onRemoveFile?.(i)} />
          ))}
        </div>
      )}

      {isRecording && recordingStream ? (
        <DictationWave stream={recordingStream} onStop={() => onToggleRecording?.()} />
      ) : (
      <Textarea
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend() } }}
        disabled={isLoading}
        placeholder={hasPending ? addMessagePlaceholder : placeholder}
        rows={large ? 2 : 1}
        className={cn(
          "min-h-0 w-full resize-none border-0 bg-transparent px-4 leading-relaxed focus-visible:ring-0 focus-visible:ring-offset-0",
          large ? "pt-4 pb-2" : "pt-3.5 pb-1.5"
        )}
        style={{ maxHeight: 160 }}
      />
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between px-2.5 pb-2.5">
        {/* Left: attach + agent selector */}
        <div className="flex items-center gap-1">
          {onAttach && (
            <Button type="button" size="icon-xs" variant="ghost" onClick={onAttach} disabled={isLoading}
              aria-label={t('brain.adjuntar')} title={t('brain.adjuntar')}
              className="rounded-lg text-muted-foreground hover:text-foreground">
              <Paperclip className="h-4 w-4" />
            </Button>
          )}
          {onAgentSelect && (
            <AgentSelector agents={agents ?? []} activeAgent={activeAgent ?? null} onSelect={onAgentSelect} disabled={isLoading} />
          )}
          {onModelSelect && (
            <ModelSelector models={models ?? []} chosen={chosenModel ?? null} onChoose={onModelSelect} disabled={isLoading} />
          )}
        </div>

        {/* Right: mic + send */}
        <div className="flex items-center gap-1">
          {onToggleRecording && !(isRecording && recordingStream) && (
            <Button type="button" size="icon-xs" variant="ghost" onClick={onToggleRecording} disabled={isLoading || isTranscribing}
              aria-label={t(isTranscribing ? 'brain.dictado.transcribiendo' : isRecording ? 'brain.dictado.parar' : 'brain.dictado.empezar')}
              title={t(isTranscribing ? 'brain.dictado.transcribiendo' : isRecording ? 'brain.dictado.parar' : 'brain.dictado.empezar')}
              className={cn("rounded-lg", isRecording && "bg-destructive/10 text-destructive hover:bg-destructive/20")}>
              {isTranscribing ? <Loader2 className="h-4 w-4 animate-spin" /> : isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4 text-muted-foreground" />}
            </Button>
          )}
          {isLoading && onStop ? (
            <Button type="button" size="icon-xs" variant="secondary" onClick={onStop}
              aria-label={t('brain.detener')} title={t('brain.detener')}>
              <Square className="h-4 w-4" />
            </Button>
          ) : (
          <Button type="button" size="icon-xs" disabled={isLoading || !canSend} onClick={() => onSend()}
            className={cn(
              "rounded-lg shadow-md transition-all hover:opacity-90 hover:shadow-md disabled:opacity-20 disabled:shadow-none",
              activeAgent
                ? agentColor(activeAgent.name)
                // `bg-primary` era una clase del prototipo que aqui no
                // existe: se usa el degradado que ya usa esta pantalla.
                : "bg-primary text-primary-foreground"
            )}>
            <Send className="h-4 w-4" />
          </Button>
          )}
        </div>
      </div>
    </div>
  )
}
